import { useRouter } from 'next/router';
// @mui
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Stack,
  Typography,
  Skeleton,
} from "@mui/material";
// components
import SvgIconStyle from '../../components/SvgIconStyle';
import Label from '../../components/Label';
// hooks
import useAuth from '../../hooks/useAuth';
import useResponsive from '../../hooks/useResponsive';
// swr
import useSWR from 'swr';
// utils
import axiosSWRFetcher from '../../utils/swrFetcher';
import { fKrTrackingTimeKST } from '../../utils/formatTime';
import axios from '../../utils/axios';

export default function DailyDoneTodoDialog({ isOpen, closeDialog, row }) {
  const smUp = useResponsive('up', 'sm');
  const { user } = useAuth();
  const companyId = user?.employee.company[0].id;
  const router = useRouter();
  const { c } = router.query;

  const isRunning = !row?.end_at;
  const modified = row?.modified_start_at && row?.modified_end_at;
  const startDateTime = modified ? row?.modified_start_at : row?.start_at;
  const endDateTime = isRunning ? new Date() : modified ? row?.modified_end_at : row?.end_at;
  const duration = new Date(endDateTime) - new Date(startDateTime);
  const hours = Math.floor(duration / (1000 * 60 * 60));
  const minutes = Math.floor((duration % (1000 * 60 * 60)) / (1000 * 60));

  const timeTrackingItemId = row && row.id;
  const { data, isValidating } = useSWR((row && isOpen && timeTrackingItemId) ? `/api/works/todo/company/${companyId}/crew/${c}/share/${timeTrackingItemId}/` : null, axiosSWRFetcher, {errorRetryInterval: 2000, revalidateOnFocus: false});

  const openTimeTrackingItemFile = async (item) => {
    const response = await axios.get(`/api/works/todo/company/${companyId}/crew/${c}/share/${timeTrackingItemId}/file/${item.id}/`);
    window.open(response.data.url, '_blank', 'noopener, noreferrer');
  };

  return (
    <Dialog open={isOpen} onClose={closeDialog} fullWidth maxWidth="sm">
      <DialogContent sx={{ my: { xs: 0, sm: 1}  }}>
        {data?.is_manual && (
          <Label variant="outlined" color="primary" sx={{ mb: .5 }}>
            수동 등록함
          </Label>
        )}
        <Typography variant={smUp ? "h5" : "h6"} gutterBottom>
          근무 시간과 업무 내역
        </Typography>
        {data?.is_manual ? (
          <Typography sx={{ color: 'text.secondary' }} variant={smUp ? "body1" : "body2"}>
            크루가 수동으로 등록한 출퇴근 기록과 업무 내용입니다. 해당 기록이 실제와 다르다면, 이번주 크루의 급여를 조율 신청해 주세요.
          </Typography>
        ) : (
          <Typography sx={{ color: 'text.secondary' }} variant={smUp ? "body1" : "body2"}>
            출근 기록과 퇴근 후 완료한 일이 보여집니다.
          </Typography>
        )}
        <Box
          sx={{
            p: 3,
            my: 2,
            backgroundColor: "grey.200",
            borderRadius: 2,
          }}
        >
          {!isRunning && (
          <Typography variant={smUp ? "subtitle1" : "subtitle2" } sx={{ color: "text.secondary", mb: 0.5 }}>
            총 근무 시간
          </Typography>
          )}
          <Stack direction="row" spacing={1} alignItems="center">
            <SvgIconStyle src={`/icons/ic_clock.svg`} sx={{ width: 20, height: 20, color: "text.secondary" }} />
            <Typography variant={smUp ? "subtitle1" : "subtitle2" } sx={{ color: "text.secondary" }}>
              {hours > 0 ? `${hours}시간` : ""} {minutes}분 {isRunning && " 근무중"}
            </Typography>
          </Stack>

          <Stack direction={{ xs: "column", sm: "row" }} sx={{ mt: 3 }} spacing={smUp ? 2 : 1.5}>
            <Stack spacing={.5}
              sx={{ flexGrow: 1 }}
            >
              <Typography variant={smUp ? "subtitle1" : "subtitle2" } sx={{ color: "text.secondary" }}>
                출근 시간
              </Typography>
              <Typography variant={smUp ? "body1" : "body2" } sx={{ color: "text.secondary" }}>
                {fKrTrackingTimeKST(startDateTime)}
              </Typography>
            </Stack>
            {smUp && (
            <Stack spacing={.5}>
              <Typography variant={smUp ? "subtitle1" : "subtitle2" } sx={{ color: "text.secondary", visibility: "hidden" }}>
                -
              </Typography>
              <Typography variant={smUp ? "body1" : "body2" } sx={{ color: "text.secondary" }}>
                ~
              </Typography>
            </Stack>
            )}
            <Stack spacing={.5}
              sx={{ flexGrow: 1 }}
            >
              <Typography variant={smUp ? "subtitle1" : "subtitle2" } sx={{ color: "text.secondary" }}>
                퇴근 시간
              </Typography>
              <Typography variant={smUp ? "body1" : "body2" } sx={{ color: "text.secondary" }}>
                {isRunning ? "-" : fKrTrackingTimeKST(endDateTime)}
              </Typography>
            </Stack>
          </Stack>
        </Box>
        {isValidating && (
          <Box
            sx={{
              p: 3,
              mt: 2,
              backgroundColor: 'grey.200',
              borderRadius: 2,
            }}
          >
            <Skeleton variant="rectangular" height={100} />
          </Box>
        )}
        {!isValidating && data && (data.select.length > 0 || data.memo_at_start) && (
        <Box
          sx={{
            p: 3,
            mt: 2,
            mb: isRunning ? 0 : 2,
            backgroundColor: 'grey.200',
            borderRadius: 2,
          }}
        >
          {data.select.length > 0 && (
            <>
            <Typography variant={smUp ? "subtitle1" : "subtitle2"} sx={{ color: "text.secondary" }}>
              진행 예정 투두
            </Typography>
            <Stack spacing={1} sx={{ mt: 1 }}>
              {data && data.select.map((done, index) => (
                <Typography sx={{ color: "text.secondary" }} key={index} variant={smUp ? "body1" : "body2"}>
                  • {done.content}
                </Typography>
              ))}
            </Stack>
            </>
          )}
          {data.memo_at_start && (
            <>
            <Typography variant={smUp ? "subtitle1" : "subtitle2"} sx={{ mt: data?.select?.length > 0 ? 2 : 0, mb: 1, color: "text.secondary" }}>
              출근 메모
            </Typography>
            <Typography sx={{ color: "text.secondary", whiteSpace: "pre-wrap" }} variant={smUp ? "body1" : "body2"}>
              {data.memo_at_start}
            </Typography>
            </>
          )}
        </Box>
        )}
        {!isRunning && (
        <Box
          sx={{
            p: 3,
            mt: 2,
            backgroundColor: 'grey.200',
            borderRadius: 2,
          }}
        >
          {isValidating && (
            <Skeleton variant="rectangular" height={25} />
          )}
          {!isValidating && data && data.share?.length === 0 && !data.memo && !data.next_work_schedule && (
            <Typography variant={smUp ? "subtitle1" : "subtitle2"} sx={{ color: "text.secondary" }}>
              완료한 투두와 메모가 없습니다.
            </Typography>
          )}
          {!isValidating && data && data.share?.length > 0 && (
            <>
            <Typography variant={smUp ? "subtitle1" : "subtitle2"} sx={{ color: "text.secondary" }}>
              완료한 투두
            </Typography>
            <Stack spacing={1} sx={{ mt: 1 }}>
              {!isValidating && data && data.share.map((done, index) => (
                <Typography sx={{ color: "text.secondary" }} key={index} variant={smUp ? "body1" : "body2"}>
                  • {done.content}
                </Typography>
              ))}
            </Stack>
            </>
          )}
          {!isValidating && data && data.memo && (
            <>
            <Typography variant={smUp ? "subtitle1" : "subtitle2"} sx={{ mt: data?.share?.length > 0 ? 2 : 0, mb: 1, color: "text.secondary" }}>
              메모
            </Typography>
            <Typography sx={{ color: "text.secondary", whiteSpace: "pre-wrap", wordBreak: "break-all" }} variant={smUp ? "body1" : "body2"}>
              {data.memo}
            </Typography>
            {data?.files?.length > 0 && data.files.map((file, index) => (
              <Typography
                key={index} variant={smUp ? "subtitle1" : "subtitle2"}
                sx={{
                  color: "text.secondary", textDecoration: "underline",
                  ":hover": { cursor: "pointer" },
                }}
                onClick={() => openTimeTrackingItemFile(file)}
              >
                {file.name}
              </Typography>
            ))}
            </>
          )}
          {!isValidating && data && data.next_work_schedule && (
            <>
            <Typography variant={smUp ? "subtitle1" : "subtitle2"} sx={{ mt: data?.share?.length > 0 || data?.memo ? 2 : 0, mb: 1, color: "text.secondary" }}>
              다음 출근 계획
            </Typography>
            <Typography sx={{ color: "text.secondary", whiteSpace: "pre-wrap", wordBreak: "break-all" }} variant={smUp ? "body1" : "body2"}>
              {data.next_work_schedule}
            </Typography>
            </>
          )}
        </Box>
        )}

      </DialogContent>
      <DialogActions>
        <Button
          sx={{ px: 8 }}
          fullWidth={smUp ? false : true}
          onClick={closeDialog}
          variant="contained"
          size="large"
        >
          확인
        </Button>
      </DialogActions>
    </Dialog>
  )
}